import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import {
  ATTENDANCE_ALLOWED_SOURCE_OPTIONS,
  ATTENDANCE_SOURCE_OVERRIDE_OPTIONS,
  ATTENDANCE_TRACKING_INTERVAL_PRESET_SECONDS,
  ATTENDANCE_TIMEZONE_OPTIONS,
  AttendanceConfigFormValue,
  AttendanceConfigListResponse,
  AttendanceConfigRecord,
  AttendanceSourceRuleFormValue,
  AttendanceZoneListResponse,
  AttendanceZoneOption,
  buildAttendanceConfigPayload,
  createEmptyAttendanceConfigForm,
  createEmptyAttendanceSourceRuleForm,
  findAttendanceConfigForScope,
  extractAttendanceZones,
  formatAttendanceTrackingInterval,
  getAttendanceSourceLabelKey,
  mapAttendanceConfigToForm,
  usesDeviceRules,
  usesDuplicateWindow,
  usesGeo,
  usesMistypedNormalization,
  usesTimezone,
  usesUntypedNormalization,
  validateAttendanceConfigForm,
} from '@shared/utils/attendance-config.util';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { firstValueFrom } from 'rxjs';
import { WorkCycleConfig } from '../../attendances/work-cycle/work-cycle.interface';
import { DailyScheduleConfig } from '../../attendances/daily-schedule/daily-schedule.interface';
import { RestConfigurationConfig } from '../../attendances/rest-configuration/rest-configuration.interface';
import { DeviceRecord, DevicesResponse } from '../../devices/device.interface';
import {
  CompaniesRecord,
  CompanyWithLaborConfigPayload,
  CompanyWithLaborConfigResponse,
  CompanyWorkAssignment,
} from './companies-record.interface';

type DetailMode = 'create' | 'view' | 'edit';

interface CompanyFormValue {
  name: string;
  code: string;
  description: string;
  isActive: boolean;
}

interface WorkAssignmentFormValue {
  enabled: boolean;
  workCycleId: string;
  restConfigurationId: string;
  cycleStartDate: string;
  effectiveFrom: string;
  effectiveTo: string;
  toleranceRules: {
    entryToleranceMinutes: number | '';
    earlyExitToleranceMinutes: number | '';
    roundingMarginMinutes: number | '';
  };
  overtimePolicy: {
    ignoreOvertime: boolean;
  };
  turnPolicy: {
    enableAutoClose: boolean;
    autoCloseGraceMinutes: number | '';
  };
}

interface EffectiveOperationalConfigResponse {
  workAssignment: {
    configFrom: 'companiesRecord' | 'defaults';
    configFromId?: string | null;
    value: CompanyWorkAssignment | null;
  };
  attendanceConfig: {
    configFrom: 'companiesRecord' | 'center' | 'defaults';
    configFromId?: string | null;
    attendanceConfigId?: string | null;
    value: Omit<AttendanceConfigRecord, 'attendanceConfigId'> & { attendanceConfigId?: string };
  };
}

interface AssignmentCalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isStart: boolean;
  isEnd: boolean;
  isInRange: boolean;
  isToday: boolean;
}

// interface AssignmentCalendarMonth {
//   label: string;
//   days: Array<AssignmentCalendarDay | null>;
// }

interface AssignmentScheduleCalendarDay extends AssignmentCalendarDay {
  previewDay: WorkCyclePreviewDay | null;
  weekdayLabel: string;
}

interface AssignmentScheduleCalendarMonth {
  label: string;
  days: AssignmentScheduleCalendarDay[];
}

interface WorkCyclePreviewDay {
  dayOrder: number;
  dayType: 'work' | 'rest';
  scheduleName: string;
  scheduleRange: string;
  expectedHours: string;
  isRest: boolean;
}

@Component({
  selector: 'app-companies-record-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    DateTimePickerFieldComponent,
    LoadingStateComponent,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    TranslateModule,
  ],
  templateUrl: './companies-record-detail.component.html',
  styleUrl: './companies-record-detail.component.scss',
})
export class CompaniesRecordDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiService = inject(AuthorizedApiService);
  private authorizationService = inject(AuthorizationService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  isLoading = true;
  mode: DetailMode = 'view';
  companiesRecordId: string | null = null;
  selectedTabIndex = 0;
  assignmentScheduleMonthIndex = 0;
  company: Partial<CompaniesRecord> = {
    name: '',
    code: '',
    description: '',
    center: '',
    isActive: true,
  };
  entityForm: CompanyFormValue = this.createEmptyEntityForm();
  workAssignmentForm: WorkAssignmentFormValue = this.createEmptyWorkAssignmentForm();
  workCycles: WorkCycleConfig[] = [];
  selectedWorkCycleDetail: WorkCycleConfig | null = null;
  dailySchedules: DailyScheduleConfig[] = [];
  restConfigurations: RestConfigurationConfig[] = [];
  devices: DeviceRecord[] = [];
  selectedDeviceIds: string[] = [];
  workAssignment: CompanyWorkAssignment | null = null;
  attendanceConfig: AttendanceConfigRecord | null = null;
  attendanceConfigForm: AttendanceConfigFormValue = createEmptyAttendanceConfigForm();
  effectiveOperationalConfig: EffectiveOperationalConfigResponse | null = null;
  isRemovingOwnWorkAssignment = false;
  isRemovingOwnAttendanceConfig = false;
  zoneOptions: AttendanceZoneOption[] = [];
  readonly attendanceSourceOptions = [...ATTENDANCE_ALLOWED_SOURCE_OPTIONS];
  readonly attendanceSourceRuleOptions = [...ATTENDANCE_SOURCE_OVERRIDE_OPTIONS];
  readonly attendanceTrackingIntervalOptions = [...ATTENDANCE_TRACKING_INTERVAL_PRESET_SECONDS];
  readonly attendanceTimezoneOptions = [...ATTENDANCE_TIMEZONE_OPTIONS];
  readonly getAttendanceSourceLabelKey = getAttendanceSourceLabelKey;
  readonly formatAttendanceTrackingInterval = formatAttendanceTrackingInterval;

  async ngOnInit(): Promise<void> {
    this.resolveMode();
    await this.loadData();
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get pageTitle(): string {
    if (this.mode === 'create') {
      return this.t('companiesRecordsCatalog.dialog.createTitle');
    }

    if (this.mode === 'edit') {
      return this.t('companiesRecordsCatalog.dialog.editTitle');
    }

    return this.company.name || this.company.code || this.t('companiesRecordsCatalog.title');
  }

  get breadcrumbItems(): string[] {
    const base = [this.t('companiesRecordsCatalog.breadcrumb')];

    if (this.mode === 'create') {
      return [...base, this.t('companiesRecordsCatalog.actions.create')];
    }

    if (this.mode === 'edit') {
      return [...base, this.company.name || this.company.code || this.t('companiesRecordsCatalog.actions.edit')];
    }

    return [...base, this.company.name || this.company.code || this.t('companiesRecordsCatalog.actions.view')];
  }

  get canEdit(): boolean {
    return this.authorizationService.hasAccess({ resource: 'companiesRecords', action: 'update' });
  }

  get canCreate(): boolean {
    return this.authorizationService.hasAccess({ resource: 'companiesRecords', action: 'create' });
  }

  get canReadAttendanceConfig(): boolean {
    return (
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'read' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'access' })
    );
  }

  get canManageAttendanceConfig(): boolean {
    return (
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'create' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'update' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'delete' })
    );
  }

  get selectedWorkCycleName(): string {
    if (!this.workAssignmentForm.workCycleId) {
      return '-';
    }

    return (
      this.workCycles.find((cycle) => cycle.workCycleId === this.workAssignmentForm.workCycleId)?.name || '-'
    );
  }

  get displayedWorkAssignment(): CompanyWorkAssignment | null {
    if (this.isRemovingOwnWorkAssignment) {
      return null;
    }
    return this.workAssignmentForm.enabled
      ? this.workAssignment
      : this.effectiveOperationalConfig?.workAssignment.value || null;
  }

  get displayedAttendanceConfig(): AttendanceConfigRecord | null {
    if (this.attendanceConfig && this.attendanceConfigForm.enabled) {
      return this.attendanceConfig;
    }
    const effective = this.effectiveOperationalConfig?.attendanceConfig;
    if (!effective?.value || this.isRemovingOwnAttendanceConfig) {
      return null;
    }
    return {
      ...effective.value,
      attendanceConfigId: effective.attendanceConfigId || effective.value.attendanceConfigId || '',
    };
  }

  get selectedRestConfigurationName(): string {
    const restConfigurationId = this.displayedWorkAssignment?.restConfigurationId || this.workAssignmentForm.restConfigurationId;
    if (!restConfigurationId) {
      return '-';
    }

    return (
      this.restConfigurations.find(
        (restConfiguration) => restConfiguration.restConfigurationId === restConfigurationId
      )?.name || '-'
    );
  }

  get attendanceTrackingIntervalLabel(): string {
    const tracking = this.displayedAttendanceConfig?.tracking as { trackingIntervalSeconds?: number; trackingIntervalMinutes?: number } | undefined;
    return formatAttendanceTrackingInterval(tracking?.trackingIntervalSeconds, tracking?.trackingIntervalMinutes);
  }

  get assignmentCyclePreviewDays(): WorkCyclePreviewDay[] {
    const cycle = this.selectedWorkCycleDetail;
    if (!cycle?.days?.length) {
      return [];
    }

    return [...cycle.days]
      .sort((left, right) => Number(left.position || 0) - Number(right.position || 0))
      .map((day, index) => {
        const schedule = day.dailyScheduleId
          ? this.dailySchedules.find((item) => item.dailyScheduleId === day.dailyScheduleId) || null
          : null;
        const isRest = day.dayType === 'rest' || !schedule;

        return {
          dayOrder: Number(day.position || index + 1),
          dayType: isRest ? 'rest' : 'work',
          scheduleName: isRest ? this.t('companiesRecordsCatalog.workAssignment.cyclePreview.rest') : schedule?.name || '-',
          scheduleRange: isRest
            ? this.t('companiesRecordsCatalog.workAssignment.cyclePreview.noHours')
            : this.getDailyScheduleRangeLabel(schedule),
          expectedHours: isRest
            ? this.t('companiesRecordsCatalog.workAssignment.cyclePreview.noHours')
            : this.getDailyScheduleHoursLabel(schedule),
          isRest,
        };
      });
  }

  get assignmentScheduleMonthStarts(): Date[] {
    const start = this.getAssignmentCyclePreviewReferenceDate();
    if (!start) {
      return [];
    }

    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    if (!end) {
      return Array.from({ length: this.assignmentScheduleMonthIndex + 1 }, (_, index) =>
        new Date(start.getFullYear(), start.getMonth() + index, 1)
      );
    }
    const months: Date[] = [];
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);

    while (cursor <= lastMonth) {
      months.push(new Date(cursor));
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    return months;
  }

  get hasPreviousAssignmentScheduleMonth(): boolean {
    return this.assignmentScheduleMonthIndex > 0;
  }

  get hasNextAssignmentScheduleMonth(): boolean {
    if (!this.parseLocalDate(this.workAssignmentForm.effectiveTo) && !!this.getAssignmentCyclePreviewReferenceDate()) {
      return true;
    }
    return this.assignmentScheduleMonthIndex < this.assignmentScheduleMonthStarts.length - 1;
  }

  get assignmentSelectedScheduleMonth(): AssignmentScheduleCalendarMonth | null {
    const start = this.getAssignmentCyclePreviewReferenceDate();
    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    const cycleDays = this.assignmentCyclePreviewDays;

    if (!start || !cycleDays.length) {
      return null;
    }

    const monthStarts = this.assignmentScheduleMonthStarts;
    const index = monthStarts.length
      ? Math.min(Math.max(this.assignmentScheduleMonthIndex, 0), monthStarts.length - 1)
      : 0;
    const monthDate = end
      ? monthStarts[index]
      : new Date(start.getFullYear(), start.getMonth() + Math.max(this.assignmentScheduleMonthIndex, 0), 1);

    return this.buildAssignmentScheduleCalendarMonth(monthDate, start, end, cycleDays);
  }

  get assignmentWeekdayLabels(): string[] {
    return this.translate.currentLang?.startsWith('en')
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  }

  get attendanceConfigSourcesLabel(): string {
    const sources = this.displayedAttendanceConfig?.attendanceCapture?.allowedSources || [];
    return sources.length ? sources.map((source) => this.t(getAttendanceSourceLabelKey(source))).join(', ') : '-';
  }

  get attendanceConfigZonesLabel(): string {
    const zoneIds = this.displayedAttendanceConfig?.zones?.allowedZoneIds || [];
    if (!zoneIds.length) {
      return '-';
    }

    return zoneIds
      .map((zoneId) => this.zoneOptions.find((zone) => zone.zoneId === zoneId)?.name || zoneId)
      .join(', ');
  }

  async save(): Promise<void> {
    if (this.isViewMode) {
      return;
    }

    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isLoading = true;
    try {
      const payload = this.buildPayload();

      if (this.isCreateMode) {
        await firstValueFrom(
          this.apiService.post<CompanyWithLaborConfigResponse>('companies-records/with-operational-config', payload)
        );
        this.showNotification('snackbar-success', this.t('companiesRecordsCatalog.notifications.created'), 'bottom', 'center');
        await this.router.navigate(['/organization/companies-records']);
        return;
      }

      await firstValueFrom(
        this.apiService.patch<CompanyWithLaborConfigResponse>(
          `companies-records/${this.companiesRecordId}/with-operational-config`,
          payload
        )
      );
      await this.persistAttendanceConfig();
      this.showNotification('snackbar-success', this.t('companiesRecordsCatalog.notifications.updated'), 'bottom', 'center');
      await this.router.navigate(['/organization/companies-records']);
    } catch (error) {
      console.error('Error saving company record', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(
          error,
          this.isCreateMode
            ? this.t('companiesRecordsCatalog.notifications.createError')
            : this.t('companiesRecordsCatalog.notifications.updateError')
        ),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  async goToEdit(): Promise<void> {
    if (!this.companiesRecordId || !this.canEdit) {
      return;
    }
    await this.router.navigate(['/organization/companies-records', this.companiesRecordId, 'edit']);
  }

  async goBack(): Promise<void> {
    await this.router.navigate(['/organization/companies-records']);
  }

  onWorkAssignmentToggle(): void {
    if (!this.workAssignmentForm.enabled) {
      this.isRemovingOwnWorkAssignment = !!this.workAssignment;
      this.workAssignmentForm = this.createEmptyWorkAssignmentForm();
      this.selectedWorkCycleDetail = null;
      this.assignmentScheduleMonthIndex = 0;
      return;
    }
    this.isRemovingOwnWorkAssignment = false;
  }

  async onWorkCycleChange(): Promise<void> {
    await this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
    this.assignmentScheduleMonthIndex = 0;
  }

  onWorkAssignmentDatesChange(): void {
    this.assignmentScheduleMonthIndex = 0;
  }

  showPreviousAssignmentScheduleMonth(): void {
    if (this.hasPreviousAssignmentScheduleMonth) {
      this.assignmentScheduleMonthIndex -= 1;
    }
  }

  showNextAssignmentScheduleMonth(): void {
    if (this.hasNextAssignmentScheduleMonth) {
      this.assignmentScheduleMonthIndex += 1;
    }
  }

  openDatePicker(input: HTMLInputElement): void {
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }

    input.focus();
    input.click();
  }

  toPickerDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  fromPickerDate(value: Date | null | undefined): string {
    if (!value || Number.isNaN(value.getTime())) {
      return '';
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onWorkAssignmentDateSelected(
    target: 'cycleStartDate' | 'effectiveFrom' | 'effectiveTo',
    event: MatDatepickerInputEvent<Date>
  ): void {
    this.updateWorkAssignmentDate(target, this.fromPickerDate(event.value));
  }

  onWorkAssignmentDateChange(
    target: 'cycleStartDate' | 'effectiveFrom' | 'effectiveTo',
    value: string
  ): void {
    this.updateWorkAssignmentDate(target, value);
  }

  onAttendanceConfigToggle(): void {
    if (!this.attendanceConfigForm.enabled) {
      this.isRemovingOwnAttendanceConfig = !!this.attendanceConfig;
      this.attendanceConfigForm = this.isRemovingOwnAttendanceConfig
        ? createEmptyAttendanceConfigForm()
        : mapAttendanceConfigToForm(this.displayedAttendanceConfig);
      this.attendanceConfigForm.enabled = false;
      return;
    }
    this.isRemovingOwnAttendanceConfig = false;
  }

  onAttendanceAllowedSourcesChange(): void {
    for (const source of this.attendanceSourceRuleOptions) {
      if (!this.attendanceConfigForm.allowedSources.includes(source)) {
        this.attendanceConfigForm.sourceRules[source] = createEmptyAttendanceSourceRuleForm(source, this.attendanceConfigForm);
      }
    }
  }

  getAttendanceSourceRuleForm(source: string): AttendanceSourceRuleFormValue {
    return this.attendanceConfigForm.sourceRules[source];
  }

  isAttendanceSourceEnabled(source: string): boolean {
    return this.attendanceConfigForm.allowedSources.includes(source);
  }

  onAttendanceSourceRuleToggle(source: string): void {
    const rule = this.getAttendanceSourceRuleForm(source);
    if (!rule) {
      return;
    }

    if (rule.enabled) {
      this.attendanceConfigForm.sourceRules[source] = {
        ...rule,
        timezone: this.attendanceConfigForm.timezone,
        requireGeo: this.attendanceConfigForm.requireGeo,
        requireFaceEvidence: this.attendanceConfigForm.requireFaceEvidence,
        duplicateWindowSeconds: this.attendanceConfigForm.duplicateWindowSeconds,
        allowUntypedEvents: this.attendanceConfigForm.allowUntypedEvents,
        normalizeUntypedEvents: this.attendanceConfigForm.normalizeUntypedEvents,
        normalizeMistypedEvents: this.attendanceConfigForm.normalizeMistypedEvents,
      };
      return;
    }

    this.attendanceConfigForm.sourceRules[source] = createEmptyAttendanceSourceRuleForm(source, this.attendanceConfigForm);
  }

  attendanceSourceUsesTimezone(source: string): boolean { return usesTimezone(source); }
  attendanceSourceUsesGeo(source: string): boolean { return usesGeo(source); }
  attendanceSourceUsesDuplicateWindow(source: string): boolean { return usesDuplicateWindow(source); }
  attendanceSourceUsesUntypedNormalization(source: string): boolean { return usesUntypedNormalization(source); }
  attendanceSourceUsesMistypedNormalization(source: string): boolean { return usesMistypedNormalization(source); }
  attendanceSourceUsesDeviceRules(source: string): boolean { return usesDeviceRules(source); }

  private resolveMode(): void {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    const routeParamId = this.route.snapshot.paramMap.get('companiesRecordId');

    if (currentUrl.endsWith('/new')) {
      this.mode = 'create';
      this.companiesRecordId = null;
      return;
    }

    this.companiesRecordId = routeParamId;

    if (currentUrl.endsWith('/edit')) {
      this.mode = 'edit';
      return;
    }

    this.mode = 'view';
  }

  private async loadData(): Promise<void> {
    if (this.isCreateMode) {
      this.company = {
        name: '',
        code: '',
        description: '',
        center: '',
        isActive: true,
      };
      this.entityForm = this.createEmptyEntityForm();
      this.workAssignment = null;
      this.workAssignmentForm = this.createEmptyWorkAssignmentForm();
      this.selectedWorkCycleDetail = null;
      this.dailySchedules = [];
      this.assignmentScheduleMonthIndex = 0;
      this.attendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      this.workCycles = [];
      this.devices = [];
      this.selectedDeviceIds = [];
      this.zoneOptions = [];
      this.isLoading = false;
      return;
    }

    if (!this.companiesRecordId) {
      await this.goBack();
      return;
    }

    this.isLoading = true;
    try {
      const detail = await firstValueFrom(
        this.apiService.get<CompanyWithLaborConfigResponse>(
          `companies-records/${this.companiesRecordId}/with-operational-config`
        )
      );

      this.company = {
        companiesRecordId: detail.entity.companiesRecordId,
        center: detail.entity.center || '',
        code: detail.entity.code || '',
        name: detail.entity.name || '',
        description: detail.entity.description || '',
        isActive: detail.entity.isActive !== false,
      };
      await Promise.all([
        this.loadWorkCycles(detail.entity.companiesRecordId),
        this.loadDailySchedules(detail.entity.companiesRecordId),
        this.loadRestConfigurations(detail.entity.companiesRecordId),
        this.loadDevices(detail.entity.companiesRecordId),
        this.loadZones(detail.entity.companiesRecordId),
        this.loadAttendanceConfig(detail.entity.companiesRecordId),
      ]);
      this.entityForm = {
        name: this.company.name || '',
        code: this.company.code || '',
        description: this.company.description || '',
        isActive: this.company.isActive !== false,
      };
      this.workAssignment = detail.workAssignment || null;
      this.workAssignmentForm = this.createWorkAssignmentForm(detail.workAssignment || null);
      await this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
      this.selectedDeviceIds = (detail.devices || [])
        .map((device) => device.deviceId)
        .filter((deviceId): deviceId is string => !!deviceId);
      await this.loadEffectiveOperationalConfig(detail.entity.companiesRecordId);
    } catch (error) {
      console.error('Error loading company record detail', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('companiesRecordsCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
      await this.goBack();
    } finally {
      this.isLoading = false;
    }
  }

  private async loadWorkCycles(companiesRecordId?: string | null): Promise<void> {
    if (!companiesRecordId) {
      this.workCycles = [];
      this.selectedWorkCycleDetail = null;
      return;
    }

    try {
      this.workCycles =
        (await firstValueFrom(
          this.apiService.get<WorkCycleConfig[]>('labor-config/work-cycles', {
            params: {
              companiesRecordId,
              isActive: 'true',
            },
          })
        )) || [];
    } catch (error) {
      console.error('Error loading work cycles for company detail', error);
      this.workCycles = [];
      this.selectedWorkCycleDetail = null;
    }
  }

  private async loadDailySchedules(companiesRecordId?: string | null): Promise<void> {
    if (!companiesRecordId) {
      this.dailySchedules = [];
      return;
    }

    try {
      this.dailySchedules =
        (
          await firstValueFrom(
            this.apiService.get<DailyScheduleConfig[]>('labor-config/daily-schedules', {
              params: { companiesRecordId, isActive: 'true' },
            })
          )
        )
          ?.filter((schedule) => schedule.isActive !== false)
          .sort((left, right) => left.name.localeCompare(right.name)) || [];
    } catch (error) {
      console.error('Error loading daily schedules for company detail', error);
      this.dailySchedules = [];
    }
  }

  private async loadSelectedWorkCycleDetail(workCycleId: string): Promise<void> {
    if (!workCycleId) {
      this.selectedWorkCycleDetail = null;
      return;
    }

    const baseCycle = this.workCycles.find((cycle) => cycle.workCycleId === workCycleId) || null;
    if (baseCycle?.days?.length) {
      this.selectedWorkCycleDetail = baseCycle;
      return;
    }

    try {
      this.selectedWorkCycleDetail = await firstValueFrom(
        this.apiService.get<WorkCycleConfig>(`labor-config/work-cycles/${workCycleId}`)
      );
    } catch (error) {
      console.error('Error loading selected work cycle detail for company detail', error);
      this.selectedWorkCycleDetail = baseCycle;
    }
  }

  private async loadRestConfigurations(companiesRecordId?: string | null): Promise<void> {
    if (!companiesRecordId) {
      this.restConfigurations = [];
      return;
    }

    try {
      this.restConfigurations =
        (
          await firstValueFrom(
            this.apiService.get<RestConfigurationConfig[]>('labor-config/rest-configurations', {
              params: { companiesRecordId, isActive: 'true' },
            })
          )
        )
          ?.filter((restConfiguration) => restConfiguration.isActive !== false)
          .sort((left, right) => left.name.localeCompare(right.name)) || [];

      if (
        this.workAssignmentForm.restConfigurationId &&
        !this.restConfigurations.some(
          (restConfiguration) => restConfiguration.restConfigurationId === this.workAssignmentForm.restConfigurationId
        )
      ) {
        this.workAssignmentForm.restConfigurationId = '';
      }
    } catch (error) {
      console.error('Error loading rest configurations for company detail', error);
      this.restConfigurations = [];
    }
  }

  private async loadZones(companiesRecordId?: string | null): Promise<void> {
    if (!companiesRecordId) {
      this.zoneOptions = [];
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceZoneListResponse>('attendance/zones', {
          params: {
            companiesRecordId,
            isActive: 'true',
            perPage: 200,
            page: 1,
          },
        })
      );
      this.zoneOptions = extractAttendanceZones(response).filter((zone) => zone.isActive !== false);
    } catch (error) {
      console.error('Error loading attendance zones for company detail', error);
      this.zoneOptions = [];
    }
  }

  private async loadDevices(companiesRecordId?: string | null): Promise<void> {
    if (!companiesRecordId) {
      this.devices = [];
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<DevicesResponse>('devices', {
          params: {
            companiesRecordId,
            perPage: 200,
            page: 1,
          },
        })
      );
      this.devices = (response?.data || []).sort((left, right) =>
        (left.name || left.serial || '').localeCompare(right.name || right.serial || '')
      );
    } catch (error) {
      console.error('Error loading devices for company detail', error);
      this.devices = [];
    }
  }

  private async loadAttendanceConfig(companiesRecordId?: string | null): Promise<void> {
    if (!companiesRecordId || !this.canReadAttendanceConfig) {
      this.attendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceConfigListResponse>('attendance/configs', {
          params: { companiesRecordId },
        })
      );
      this.attendanceConfig = findAttendanceConfigForScope(response, { companiesRecordId });
      this.attendanceConfigForm = mapAttendanceConfigToForm(this.attendanceConfig);
    } catch (error) {
      console.error('Error loading attendance config for company detail', error);
      this.attendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
    }
  }

  private async loadEffectiveOperationalConfig(companiesRecordId: string): Promise<void> {
    try {
      this.effectiveOperationalConfig = await firstValueFrom(
        this.apiService.get<EffectiveOperationalConfigResponse>(
          `operational-config/effective/companiesRecord/${encodeURIComponent(companiesRecordId)}`
        )
      );
      if (!this.workAssignmentForm.enabled) {
        this.workAssignmentForm = this.createWorkAssignmentForm(
          this.effectiveOperationalConfig.workAssignment.value
        );
        this.workAssignmentForm.enabled = false;
        void this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
      }
      if (!this.attendanceConfigForm.enabled) {
        this.attendanceConfigForm = mapAttendanceConfigToForm(this.displayedAttendanceConfig);
        this.attendanceConfigForm.enabled = false;
      }
    } catch (error) {
      console.error('Error loading effective operational config for company', error);
      this.effectiveOperationalConfig = null;
    }
  }

  private createEmptyEntityForm(): CompanyFormValue {
    return {
      name: '',
      code: '',
      description: '',
      isActive: true,
    };
  }

  private createEmptyWorkAssignmentForm(): WorkAssignmentFormValue {
    return {
      enabled: false,
      workCycleId: '',
      restConfigurationId: '',
      cycleStartDate: '',
      effectiveFrom: '',
      effectiveTo: '',
      toleranceRules: {
        entryToleranceMinutes: '',
        earlyExitToleranceMinutes: '',
        roundingMarginMinutes: '',
      },
      overtimePolicy: {
        ignoreOvertime: false,
      },
      turnPolicy: {
        enableAutoClose: false,
        autoCloseGraceMinutes: '',
      },
    };
  }

  private createWorkAssignmentForm(workAssignment: CompanyWorkAssignment | null): WorkAssignmentFormValue {
    if (!workAssignment) {
      return this.createEmptyWorkAssignmentForm();
    }

    return {
      enabled: true,
      workCycleId: workAssignment.workCycleId || '',
      restConfigurationId: workAssignment.restConfigurationId || '',
      cycleStartDate: workAssignment.cycleStartDate || '',
      effectiveFrom: workAssignment.effectiveFrom || workAssignment.cycleStartDate || '',
      effectiveTo: workAssignment.effectiveTo || '',
      toleranceRules: {
        entryToleranceMinutes: workAssignment.toleranceRules?.entryToleranceMinutes ?? '',
        earlyExitToleranceMinutes: workAssignment.toleranceRules?.earlyExitToleranceMinutes ?? '',
        roundingMarginMinutes: workAssignment.toleranceRules?.roundingMarginMinutes ?? '',
      },
      overtimePolicy: {
        ignoreOvertime: !!workAssignment.overtimePolicy?.ignoreOvertime,
      },
      turnPolicy: {
        enableAutoClose: !!workAssignment.turnPolicy?.enableAutoClose,
        autoCloseGraceMinutes: workAssignment.turnPolicy?.autoCloseGraceMinutes ?? '',
      },
    };
  }

  private validateForm(): string | null {
    if (!this.entityForm.name.trim() || !this.entityForm.code.trim() || !this.entityForm.description.trim()) {
      return this.t('companiesRecordsCatalog.notifications.validationError');
    }

    if (this.workAssignmentForm.enabled) {
      if (!this.workAssignmentForm.workCycleId) {
        return this.t('companiesRecordsCatalog.notifications.validationWorkCycle');
      }

      if (!this.workAssignmentForm.cycleStartDate) {
        return this.t('workAssignmentRules.validationCycleStartDate');
      }

      if (!this.hasValidWorkAssignmentDateRange()) {
        return this.t('workAssignmentRules.validationDateRange');
      }
    }

    if (this.canManageAttendanceConfig && this.attendanceConfigForm.enabled) {
      const attendanceConfigValidationKey = validateAttendanceConfigForm(this.attendanceConfigForm);
      if (attendanceConfigValidationKey) {
        return this.t(attendanceConfigValidationKey);
      }
    }

    return null;
  }

  private async persistAttendanceConfig(): Promise<void> {
    if (!this.companiesRecordId || !this.canManageAttendanceConfig) {
      return;
    }

    if (!this.attendanceConfigForm.enabled) {
      if (this.attendanceConfig?.attendanceConfigId) {
        await firstValueFrom(this.apiService.delete(`attendance/configs/${this.attendanceConfig.attendanceConfigId}`));
      }
      this.attendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      return;
    }

    const payload = buildAttendanceConfigPayload(this.attendanceConfigForm, {
      companiesRecordId: this.companiesRecordId,
    });

    if (!payload) {
      return;
    }

    if (this.attendanceConfig?.attendanceConfigId) {
      this.attendanceConfig = await firstValueFrom(
        this.apiService.patch<AttendanceConfigRecord>(`attendance/configs/${this.attendanceConfig.attendanceConfigId}`, payload)
      );
      return;
    }

    this.attendanceConfig = await firstValueFrom(
      this.apiService.post<AttendanceConfigRecord>('attendance/configs', payload)
    );
  }

  private buildPayload(): CompanyWithLaborConfigPayload {
    const payload: CompanyWithLaborConfigPayload = {
      entity: {
        name: this.entityForm.name.trim(),
        code: this.entityForm.code.trim(),
        description: this.entityForm.description.trim(),
      },
      deviceIds: [...this.selectedDeviceIds],
    };

    if (!this.isCreateMode) {
      payload.entity.isActive = this.entityForm.isActive;
    }

    if (this.workAssignmentForm.enabled) {
      payload.workAssignment = {
        workCycleId: this.workAssignmentForm.workCycleId || null,
        restConfigurationId: this.workAssignmentForm.restConfigurationId.trim() || null,
        cycleStartDate: this.workAssignmentForm.cycleStartDate,
        // cycleOffset is intentionally omitted until offset-based assignments are enabled again.
        ...(this.shouldSendEffectiveFrom() ? { effectiveFrom: this.workAssignmentForm.effectiveFrom } : {}),
        effectiveTo: this.workAssignmentForm.effectiveTo || null,
        toleranceRules: {
          entryToleranceMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.entryToleranceMinutes),
          earlyExitToleranceMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.earlyExitToleranceMinutes),
          roundingMarginMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.roundingMarginMinutes),
        },
        overtimePolicy: {
          ignoreOvertime: this.workAssignmentForm.overtimePolicy.ignoreOvertime,
        },
        turnPolicy: {
          enableAutoClose: this.workAssignmentForm.turnPolicy.enableAutoClose,
          ...(this.workAssignmentForm.turnPolicy.autoCloseGraceMinutes !== ''
            ? { autoCloseGraceMinutes: Number(this.workAssignmentForm.turnPolicy.autoCloseGraceMinutes) }
            : {}),
        },
      };
    } else if (!this.isCreateMode && this.workAssignment) {
      payload.workAssignment = null;
    }

    return payload;
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private toNullableNumber(value: number | '' | null | undefined): number | null {
    return value === '' || value === null || value === undefined ? null : Number(value);
  }

  private buildAssignmentScheduleCalendarMonth(
    monthDate: Date,
    start: Date,
    end: Date | null,
    cycleDays: WorkCyclePreviewDay[]
  ): AssignmentScheduleCalendarMonth {
    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const gridStart = this.startOfWeek(monthStart);
    const gridEnd = this.endOfWeek(monthEnd);
    const today = this.parseLocalDate(this.toIsoDateString(new Date()));
    const weekdayFormatter = new Intl.DateTimeFormat(this.getCalendarLocale(), { weekday: 'long' });
    const days: AssignmentScheduleCalendarDay[] = [];

    for (let cursor = new Date(gridStart); cursor <= gridEnd; cursor = this.addDays(cursor, 1)) {
      const isInRange = cursor >= start && (!end || cursor <= end);
      const weekdayLabel = weekdayFormatter.format(cursor);
      days.push({
        date: new Date(cursor),
        dayNumber: cursor.getDate(),
        isCurrentMonth: cursor.getMonth() === monthStart.getMonth(),
        isStart: this.isSameDate(cursor, start),
        isEnd: !!end && this.isSameDate(cursor, end),
        isInRange,
        isToday: !!today && this.isSameDate(cursor, today),
        previewDay: isInRange ? this.getAppliedCycleDayForDate(cursor, cycleDays) : null,
        weekdayLabel: weekdayLabel.charAt(0).toUpperCase() + weekdayLabel.slice(1),
      });
    }

    return {
      label: new Intl.DateTimeFormat(this.getCalendarLocale(), { month: 'long', year: 'numeric' }).format(monthStart),
      days,
    };
  }

  private parseLocalDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }

    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private startOfWeek(value: Date): Date {
    const normalized = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const day = normalized.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    normalized.setDate(normalized.getDate() + mondayOffset);
    return normalized;
  }

  private endOfWeek(value: Date): Date {
    return this.addDays(this.startOfWeek(value), 6);
  }

  private addDays(value: Date, days: number): Date {
    const next = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    next.setDate(next.getDate() + days);
    return next;
  }

  private isSameDate(left: Date, right: Date): boolean {
    return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
  }

  private getCalendarLocale(): string {
    return this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
  }

  private getDailyScheduleRangeLabel(schedule: DailyScheduleConfig | null): string {
    if (!schedule) {
      return this.t('companiesRecordsCatalog.workAssignment.cyclePreview.noHours');
    }

    if (schedule.mode === 'entry_window_duration') {
      if (schedule.entryWindowStartTime && schedule.entryWindowEndTime) {
        return `${schedule.entryWindowStartTime} - ${schedule.entryWindowEndTime}`;
      }
      return this.t('companiesRecordsCatalog.workAssignment.cyclePreview.flexibleWindow');
    }

    if (schedule.mode === 'split_shift') {
      if (schedule.blocks?.length) {
        return schedule.blocks.map((block) => `${block.startTime} - ${block.endTime}`).join(' · ');
      }
      return this.t('companiesRecordsCatalog.workAssignment.cyclePreview.splitShift');
    }

    if (schedule.startTime && schedule.endTime) {
      return `${schedule.startTime} - ${schedule.endTime}`;
    }

    return this.t('companiesRecordsCatalog.workAssignment.cyclePreview.noHours');
  }

  private getDailyScheduleHoursLabel(schedule: DailyScheduleConfig | null): string {
    if (!schedule) {
      return this.t('companiesRecordsCatalog.workAssignment.cyclePreview.noHours');
    }

    const totalMinutes = this.getDailyScheduleIntervals(schedule).reduce(
      (sum, interval) => sum + Math.max(0, interval.end - interval.start),
      0
    );
    if (totalMinutes <= 0) {
      return this.t('companiesRecordsCatalog.workAssignment.cyclePreview.noHours');
    }

    const hours = totalMinutes / 60;
    return Number.isInteger(hours)
      ? this.t('companiesRecordsCatalog.workAssignment.cyclePreview.hoursWhole', { hours })
      : this.t('companiesRecordsCatalog.workAssignment.cyclePreview.hoursFraction', { hours: hours.toFixed(1) });
  }

  private getDailyScheduleIntervals(schedule: DailyScheduleConfig): Array<{ start: number; end: number }> {
    if (schedule.blocks?.length) {
      return schedule.blocks
        .map((block) => this.createMinuteInterval(block.startTime, block.endTime))
        .filter((interval): interval is { start: number; end: number } => !!interval);
    }

    const interval =
      schedule.mode === 'entry_window_duration'
        ? this.createMinuteInterval(schedule.entryWindowStartTime, schedule.entryWindowEndTime)
        : this.createMinuteInterval(schedule.startTime, schedule.endTime);
    return interval ? [interval] : [];
  }

  private createMinuteInterval(startTime?: string | null, endTime?: string | null): { start: number; end: number } | null {
    const start = this.normalizeMinuteValue(startTime);
    const end = this.normalizeMinuteValue(endTime);

    if (start === null || end === null) {
      return null;
    }

    return end > start ? { start, end } : { start, end: end + 1440 };
  }

  private normalizeMinuteValue(value?: string | null): number | null {
    if (!value || !value.includes(':')) {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }

    return hours * 60 + minutes;
  }

  private getAssignmentCyclePreviewReferenceDate(): Date | null {
    return this.parseLocalDate(this.workAssignmentForm.effectiveFrom) || this.parseLocalDate(this.workAssignmentForm.cycleStartDate) || null;
  }

  private getAppliedCycleDayForDate(date: Date, cycleDays: WorkCyclePreviewDay[]): WorkCyclePreviewDay {
    const cycleLength = cycleDays.length;
    const cycleStartDate = this.parseLocalDate(this.workAssignmentForm.cycleStartDate) || this.getAssignmentCyclePreviewReferenceDate() || date;
    const daysDiff = Math.floor((date.getTime() - cycleStartDate.getTime()) / 86400000);
    const normalizedIndex = this.mod(daysDiff, cycleLength);
    const cycleDay = cycleDays[normalizedIndex] || cycleDays[0];

    return this.isRestConfiguredForDate(date, cycleDay)
      ? this.toConfiguredRestPreviewDay(cycleDay)
      : cycleDay;
  }

  private isRestConfiguredForDate(date: Date, cycleDay: WorkCyclePreviewDay): boolean {
    if (cycleDay.isRest) {
      return true;
    }

    const restConfiguration = this.getSelectedRestConfiguration();
    if (!restConfiguration || restConfiguration.isActive === false) {
      return false;
    }

    const mode = String(restConfiguration.mode || '').trim().toLowerCase();
    if (mode === 'fixed_weekday') {
      const weekday = date.getDay() === 0 ? 7 : date.getDay();
      return (restConfiguration.weekdays || []).map(Number).includes(weekday);
    }

    if (mode === 'cycle_reference') {
      if (
        restConfiguration.cycleReference &&
        restConfiguration.cycleReference !== this.workAssignmentForm.workCycleId
      ) {
        return false;
      }

      return (restConfiguration.restDays || []).map(Number).includes(cycleDay.dayOrder);
    }

    return false;
  }

  private getSelectedRestConfiguration(): RestConfigurationConfig | null {
    const restConfigurationId = this.workAssignmentForm.restConfigurationId;
    if (!restConfigurationId) {
      return null;
    }

    return this.restConfigurations.find(
      (item) => item.restConfigurationId === restConfigurationId
    ) || null;
  }

  private toConfiguredRestPreviewDay(cycleDay: WorkCyclePreviewDay): WorkCyclePreviewDay {
    return {
      ...cycleDay,
      dayType: 'rest',
      scheduleName: this.t('companiesRecordsCatalog.workAssignment.cyclePreview.rest'),
      scheduleRange: this.t('companiesRecordsCatalog.workAssignment.cyclePreview.noHours'),
      expectedHours: this.t('companiesRecordsCatalog.workAssignment.cyclePreview.noHours'),
      isRest: true,
    };
  }

  private updateWorkAssignmentDate(
    target: 'cycleStartDate' | 'effectiveFrom' | 'effectiveTo',
    value: string
  ): void {
    const previousCycleStart = this.workAssignmentForm.cycleStartDate;
    this.workAssignmentForm[target] = value;
    if (
      target === 'cycleStartDate' &&
      (!this.workAssignmentForm.effectiveFrom || this.workAssignmentForm.effectiveFrom === previousCycleStart)
    ) {
      this.workAssignmentForm.effectiveFrom = value;
    }
    this.onWorkAssignmentDatesChange();
  }

  private hasValidWorkAssignmentDateRange(): boolean {
    const start = this.parseLocalDate(
      this.workAssignmentForm.effectiveFrom || this.workAssignmentForm.cycleStartDate
    );
    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    return !start || !end || end.getTime() >= start.getTime();
  }

  private shouldSendEffectiveFrom(): boolean {
    return !!this.workAssignmentForm.effectiveFrom &&
      this.workAssignmentForm.effectiveFrom !== this.workAssignmentForm.cycleStartDate;
  }

  private mod(value: number, base: number): number {
    if (!base) {
      return 0;
    }
    return ((value % base) + base) % base;
  }

  private toIsoDateString(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
